import MarkerClusterer from "@googlemaps/markerclustererplus";
import { Collapse } from "@opten/collapse/initializer";
import { addClass } from "@opten/dom/add-class";
import { getSelectorElementFromElement } from "@opten/dom/get-selector-element-from-element";
import { hasClass } from "@opten/dom/has-class";
import { removeClass } from "@opten/dom/remove-class";
import { toggleClass } from "@opten/dom/toggle-class";
import { dispatchCustomEvent } from "@opten/events/dispatch-custom-event";
import { fitBoundsWithPadding } from "@opten/map-helpers/fit-bounds-with-padding";
import { scrollToOffset } from "@opten/scroll/scroll-to-offset";
import { FilterHelper } from "./filter-helper";
import { PageTopComponent } from "./page-top";

declare const Cookiebot: any;

export interface MapSearcherOptions {
	locationClass?: string;
	cookieClass?: string;
	mapClass?: string;
	mapOverlayClass?: string;
	mapScrollClass?: string;
	mapRadiusClass?: string;
	markerIcon?: {
		url: string;
		size: google.maps.Size;
		scaledSize: google.maps.Size;
		anchor: google.maps.Point;
	};
	clusterStyle?: any;
	mapCenter?: {
		lat: number;
		lng: number;
	};
	mapInitialZoom?: 8;
	maxZoom?: number;
	mapStyles?: google.maps.MapTypeStyle[];
	radiusBounds?: number;
	openIfSingleResult?: boolean;
	hideCluster?: boolean;
	searchGeocode?: boolean;
}

declare class OverlappingMarkerSpiderfier {
	constructor(map: any, options: any);
	addMarker(marker: any);
	addListener(event, func);
	markersNearMarker(event, func);
}

interface MapsLocation {
	element: HTMLElement;
	marker: google.maps.Marker;
	markerSettings: {
		iconUrl: string;
		iconColor: string;
		iconColorActive: string;
		iconColorHit: string;
	};
	latLng: google.maps.LatLng;
	tags: string[];
	search: string[];
	type: "vbgf" | "sn21" | "canton" | "standard";
	visible: boolean;
}

interface MapsFeature {
	name: string;
	feature: google.maps.Data.Feature;
	bounds: google.maps.LatLngBounds;
}

export class MapSearcher {
	public defaultOptions: MapSearcherOptions = {
		cookieClass: "js-map-filter-cookie",
		mapClass: "js-map-filter-map",
		mapOverlayClass: "js-map-filter-overlay",
		mapScrollClass: "js-map-filter-scroll",
		mapRadiusClass: "js-map-filter-radius",
		locationClass: "js-map-filter-location",
		markerIcon: {
			url: "/map/map-marker.svg",
			size: new google.maps.Size(22, 35),
			scaledSize: new google.maps.Size(22, 35),
			anchor: new google.maps.Point(11, 35),
		},
		clusterStyle: [
			{
				url: "/map/map-cluster.png",
				height: 52,
				width: 53,
				textColor: "black",
			},
			{
				url: "/map/map-cluster2.png",
				height: 55,
				width: 56,
				textColor: "black",
			},
			{
				url: "/map/map-cluster3.png",
				height: 65,
				width: 66,
				textColor: "black",
			},
		],
		mapCenter: { lat: 46.905358, lng: 8.423689 },
		mapInitialZoom: 8,
		maxZoom: 15,
		mapStyles: [],
		radiusBounds: 0,
		openIfSingleResult: true,
		hideCluster: false,
		searchGeocode: true,
	};

	public filter: FilterHelper;
	private cookiePlaceholder: HTMLElement;
	private mapPlaceholder: HTMLElement;
	private mapOverlay: HTMLElement;
	private mapScroll: HTMLElement;
	private mapRadiusField?: HTMLFormElement;
	private options: MapSearcherOptions;
	private map: google.maps.Map;
	private oms?: OverlappingMarkerSpiderfier;
	private markerCluster?: MarkerClusterer;
	private geoCoder: google.maps.Geocoder;
	private infowindow: google.maps.InfoWindow;
	private locations: MapsLocation[] = [];
	private previousAddress: string;
	private previousBounds: google.maps.LatLngBounds;
	private cantons: MapsFeature[] = [];
	private filterType: "Default" | "HealthyCommunities" | "HealthySchools" =
		"Default";

	// tslint:disable-next-line: max-func-body-length
	constructor(
		private element: HTMLElement,
		private scrollWrapper: HTMLElement | Window,
		options?: MapSearcherOptions,
	) {
		this.options = { ...this.defaultOptions, ...options };

		this.filter = new FilterHelper(this.element, {
			onSubmit: () => {
				this.search();
			},
			onReset: () => {
				this.search();
			},
			onRemoveFilter: () => {
				this.search();
			},
		});

		this.cookiePlaceholder = this.element.querySelector(
			`.${this.options.cookieClass}`,
		);
		this.mapPlaceholder = this.element.querySelector(
			`.${this.options.mapClass}`,
		);
		this.mapOverlay = this.element.querySelector(
			`.${this.options.mapOverlayClass}`,
		);
		this.mapScroll = this.element.querySelector(
			`.${this.options.mapScrollClass}`,
		);
		this.mapRadiusField = this.element.querySelector(
			`.${this.options.mapRadiusClass}`,
		);
		new PageTopComponent(
			document.querySelector(".js-page-top"),
			this.mapScroll,
		);

		this.filterType =
			this.filter.form.getAttribute("data-type") || "Default";

		this.options.radiusBounds = parseInt(
			this.mapRadiusField?.getAttribute("data-radius") ||
				this.options.radiusBounds?.toString() ||
				"0",
		);
		this.options.hideCluster =
			this.mapPlaceholder.getAttribute("data-hide-cluster") == "true";

		this.updateValuesByUrl();

		this.locations = Array.from(
			this.element.getElementsByClassName(this.options.locationClass),
		)
			.map((elem) => this.createMapLocation(elem))
			.filter((o) => !!o);

		this.locations.forEach((l) => {
			let markerClick = false;
			l.visible = true;

			const collapse = getSelectorElementFromElement(l.element);
			const overview = collapse?.querySelector(".js-map-filter-overview");
			if (overview) {
				overview.addEventListener("click", (e) => {
					e.preventDefault();
					dispatchCustomEvent(l.element, "click");
					this.centerMap();
					if (window.innerWidth < 768) {
						scrollToOffset(
							this.getScrollTop() * -1 + 187,
							undefined,
							300,
							0,
							this.getScrollTarget(),
						);
					}
				});
			}
			l.marker.addListener("spider_click", () => {
				markerClick = true;
				dispatchCustomEvent(l.element, "click");
			});
			l.element.addEventListener("click", (e) => {
				e.preventDefault();
				const isActive = !hasClass(l.element, "is-active");
				const actives = this.locations.filter((loc) =>
					hasClass(loc.element, "is-active"),
				);
				if (actives && actives.length) {
					actives.forEach((active) => {
						this.hideCollapse(active);
					});
				}
				if (isActive) {
					if (markerClick === false) {
						this.centerMap(
							new google.maps.LatLngBounds(l.latLng, l.latLng),
							true,
						);
					}
					this.showCollapse(l, () => {
						scrollToOffset(
							l.element.offsetTop - this.getScrollTop(),
							undefined,
							300,
							-15,
							this.getScrollTarget(),
						);
					});
				} else {
					this.hideCollapse(l);
				}
				markerClick = false;
			});
		});

		this.mapRadiusField?.addEventListener("change", (e) => {
			const radius = parseInt(this.mapRadiusField.value || "0");
			if (!isNaN(radius)) {
				this.options.radiusBounds = radius;
				this.search();
			}
		});

		window.addEventListener(
			"CookiebotOnLoad",
			(e) => {
				this.cookiebotInit();
			},
			false,
		);
		this.cookiebotInit();

		this.search();
	}

	private cookiebotInit() {
		if ("Cookiebot" in window && Cookiebot.consent.marketing) {
			removeClass(this.mapPlaceholder, "d-none");
			addClass(this.cookiePlaceholder, "d-none");
		} else {
			addClass(this.mapPlaceholder, "d-none");
			removeClass(this.cookiePlaceholder, "d-none");
		}
		if (
			"Cookiebot" in window &&
			Cookiebot.consent.marketing &&
			hasClass(this.element, "is-cookiebot-initialized") == false
		) {
			addClass(this.element, "is-cookiebot-initialized");

			this.infowindow = new google.maps.InfoWindow({
				content: "",
			});

			this.map = new google.maps.Map(this.mapPlaceholder, {
				zoom: this.options.mapInitialZoom,
				center: this.options.mapCenter,
				fullscreenControl: false,
				mapTypeControl: false,
				streetViewControl: false,
				styles: this.options.mapStyles,
				gestureHandling: window.innerWidth > 767 ? "greedy" : "auto",
			});
			google.maps.event.addListenerOnce(this.map, "idle", () =>
				this.centerMap(),
			);
			google.maps.event.addListener(this.map, "click", () => {
				this.infowindow.close();
			});

			if (this.options.hideCluster == false) {
				this.oms = new OverlappingMarkerSpiderfier(this.map, {
					markersWontMove: true,
					markersWontHide: true,
					basicFormatEvents: true,
					//keepSpiderfied: true,
					circleFootSeparation: Math.max(
						this.options.markerIcon.size.width,
						this.options.markerIcon.size.height,
					),
					nearbyDistance: Math.max(
						this.options.markerIcon.size.width,
						this.options.markerIcon.size.height,
					),
				});

				this.markerCluster = new MarkerClusterer(this.map, [], {
					maxZoom: this.options.maxZoom - 1,
					styles: this.options.clusterStyle,
					zoomOnClick: false,
				});
				this.markerCluster.setCalculator((markers, numStyles) => {
					if (markers.length >= 100) {
						return { text: markers.length, index: 3 };
					} else if (markers.length >= 10) {
						return { text: markers.length, index: 2 };
					} else {
						return { text: markers.length, index: 1 };
					}
				});
				this.markerCluster.addListener("clusterclick", (cluster) => {
					const theBounds = cluster.getBounds();
					setTimeout(() => {
						this.centerMap(theBounds);
					}, 100);
				});
			}

			this.geoCoder = new google.maps.Geocoder();

			this.map.data.loadGeoJson(
				"/map/cantons_small.json",
				null,
				(features) => {
					this.cantons = features.map((feature) => {
						var bounds = new google.maps.LatLngBounds();
						feature
							.getGeometry()
							.forEachLatLng((latLng) => bounds.extend(latLng));
						return {
							name: feature.getProperty("ak").toLowerCase(),
							feature,
							bounds,
						} as MapsFeature;
					});
					this.search();
				},
			);
			this.map.data.setStyle({
				strokeColor: "#AE0270",
				strokeOpacity: 0.8,
				strokeWeight: 0,
				fillColor: "#AE0270",
				fillOpacity: 0.1,
				clickable: false,
			});

			this.locations.forEach((l) => {
				l.marker.setMap(this.map);
				if (this.options.hideCluster == false) {
					this.oms.addMarker(l.marker);
					this.markerCluster.addMarker(l.marker);
				}
			});
		}
	}

	updateValuesByUrl(hash?: string) {
		const hashArray = (hash || location.hash).toLowerCase().split("/");
		if (hashArray && hashArray.length > 0) {
			this.filter.clear();
			hashArray.forEach((item) => {
				if (item.startsWith("#")) {
					// skip
				} else if (item.startsWith(".")) {
					// filter
					for (const input of this.filter.filters) {
						if (input) {
							if (input.value === item) {
								input.checked = true;
								const event = new Event("change", {
									bubbles: false,
									cancelable: true,
								});
								input.dispatchEvent(event);
								break;
							} else if (input.options) {
								const option = Array.from(input.options).find(
									(o) => o.value == item,
								);
								if (option) {
									option.selected = true;
								}
							}
						}
					}
				} else {
					const dateField = this.filter.filters.find(
						(o) =>
							o.type === "date" || hasClass(o, "js-datepicker"),
					);
					if (dateField && Date.parse(item) > 0) {
						dateField.value = item;
						const event = new Event("change", {
							bubbles: false,
							cancelable: true,
						});
						dateField.dispatchEvent(event);
					} else if (this.filter.search) {
						this.filter.search.value = decodeURI(item);
						dispatchCustomEvent(this.filter.search, "input");
					}
				}
			});
		}
	}

	reInit() {
		setTimeout(() => {
			this.updateValuesByUrl();
			this.search();
			google.maps.event.trigger(this.map, "resize");
		}, 300);
	}

	getActiveLocations(): MapsLocation[] {
		return this.locations.filter((o) => o.visible);
	}

	private createMapLocation(element: Element) {
		const lat = parseFloat(element.getAttribute("data-lat"));
		const lng = parseFloat(element.getAttribute("data-lng"));
		const address = element.getAttribute("data-address");
		const name = element.getAttribute("data-name");
		const tagsAttr = element.getAttribute("data-tags");
		const searchAttr = element.getAttribute("data-search");
		const type = element.getAttribute("data-type");
		const iconUrl = element.getAttribute("data-icon");
		const iconColor = element.getAttribute("data-icon-color");
		const iconColorActive = element.getAttribute("data-icon-color-active");
		const iconColorHit = element.getAttribute("data-icon-color-hit");
		let tags = [];
		if (tagsAttr) {
			tags = tagsAttr.split(",");
		}
		let search = [];
		if (searchAttr) {
			search = searchAttr.split(",");
		}
		if (lat && lng) {
			return this.setupMarker(
				element,
				tags,
				search,
				new google.maps.LatLng(lat, lng),
				type,
				iconUrl,
				iconColor,
				iconColorActive,
				iconColorHit,
			);
		}
	}
	private setupMarker(
		element,
		tags,
		search,
		location,
		type,
		iconUrl,
		iconColor,
		iconColorActive,
		iconColorHit,
	): MapsLocation {
		const marker = new google.maps.Marker({
			position: location,
			icon: this.getMarkerTemplate(iconColor, iconUrl),
			title: element.getAttribute("data-name"),
		});
		return {
			element,
			latLng: location,
			tags,
			search,
			marker,
			markerSettings: {
				iconUrl,
				iconColor,
				iconColorActive,
				iconColorHit,
			},
			type,
			visible: false,
		};
	}

	private setUrlHash(hash) {
		if (
			(location.hash && location.hash != hash) ||
			(!location.hash && hash != "#/")
		) {
			if (history.pushState) {
				history.pushState(null, null, hash);
			} else {
				location.hash = hash;
			}
		}
	}

	// tslint:disable-next-line: max-func-body-length
	public search() {
		const formData = this.filter.filters
			.filter((o) =>
				o.type == "checkbox" || o.type == "radio" ? o.checked : o,
			)
			.map((o) => [o.name, o.value])
			.filter((o) => !!o[1]);
		const activeFilters = formData.map((o) => o[1]);
		const activeFiltersGrouped: any = formData.reduce((arr: any[], ob) => {
			const x = arr.find((o) => o[0] === ob[0]);
			x ? x[1].push(ob[1]) : arr.push([ob[0], [ob[1]]]);
			return arr;
		}, []);
		const activeCantons = this.cantons.filter(
			(o) => activeFilters.indexOf("." + o.name) >= 0,
		);
		// tslint:disable-next-line: max-func-body-length
		this.getSearchBounds(activeFilters, activeCantons).then((bounds) => {
			"Cookiebot" in window &&
				Cookiebot.consent.marketing &&
				this.options.hideCluster == false &&
				this.markerCluster.clearMarkers();
			const searchAddress = this.getSearchAddress();
			const hash = `${this.element.id}/${activeFilters.join("/")}`;
			if (hash) {
				this.setUrlHash(`#${hash}`);
			}

			this.filterLocations(
				this.locations,
				activeFilters,
				activeFiltersGrouped,
				activeCantons,
				searchAddress,
			);

			var locationBounds = new google.maps.LatLngBounds().union(
				bounds ||
					this.accumulateLocationBounds(this.locations, false) ||
					new google.maps.LatLngBounds(),
			);
			if (
				locationBounds &&
				locationBounds.isEmpty() == false &&
				this.options.radiusBounds > 0
			) {
				var radiusBounds = this.addRadiusToBounds(
					this.options.radiusBounds,
					locationBounds,
				);
				var furtherLocations = this.locations.filter(
					(l) =>
						radiusBounds.contains(l.latLng) && l.visible == false,
				);
				if (furtherLocations && furtherLocations.length > 0) {
					const activeFiltersWithoutSearch =
						// activeFilters.length == 1 && !!searchAddress
						// 	? activeFilters.filter((o) => o !== searchAddress)
						// 	: activeFilters;
						activeFilters;
					this.filterLocations(
						furtherLocations,
						activeFiltersWithoutSearch,
						activeFiltersGrouped,
						activeCantons,
						searchAddress,
						!!bounds,
					);
				}
			}

			if (!bounds) {
				bounds = new google.maps.LatLngBounds();
			}
			this.cantons.forEach((canton) => {
				const isActiveCanton =
					activeFilters.indexOf("." + canton.name) >= 0 ||
					this.locations.some(
						(o) =>
							o.visible &&
							o.tags[0].indexOf("." + canton.name) >= 0,
					);
				if (isActiveCanton) {
					bounds.extend(canton.bounds.getNorthEast());
					bounds.extend(canton.bounds.getSouthWest());
				}
				this.map.data.overrideStyle(canton.feature, {
					visible: isActiveCanton,
				});
			});

			this.filter.updateTags();
			this.filter.toggleEmptyState(
				this.locations.filter((o) => o.visible).length > 0,
			);

			const activeLocations = this.locations.filter((o) => o.visible);
			if (
				this.options.openIfSingleResult &&
				activeLocations.length === 1
			) {
				dispatchCustomEvent(activeLocations[0].element, "click");
			} else if (activeLocations.length > 1) {
				const actives = this.locations.filter((loc) =>
					hasClass(loc.element, "is-active"),
				);
				if (actives && actives.length) {
					actives.forEach((active) => {
						this.hideCollapse(active);
					});
				}
			}

			this.centerMap(bounds);
		});
	}

	private filterLocations(
		locations: MapsLocation[],
		activeFilters: string[],
		activeFiltersGrouped: any[],
		activeCantons: MapsFeature[],
		searchAddress: string,
		hasSearchBounds: boolean = false,
	) {
		switch (this.filterType) {
			case "HealthyCommunities":
				locations.forEach((l) => {
					if (
						(activeCantons.length > 0 &&
							activeFiltersGrouped.length -
								(searchAddress ? 1 : 0) ===
								1 &&
							(l.type === "standard" || l.type === "vbgf")) ||
						(activeCantons.length > 0 &&
							activeFiltersGrouped.length -
								(searchAddress ? 1 : 0) >
								1 &&
							(l.type === "standard" || l.type === "canton")) ||
						(activeCantons.length === 0 && l.type === "standard")
					) {
						this.searchCondition(
							l,
							activeFilters,
							activeFiltersGrouped,
							searchAddress,
							hasSearchBounds,
						);
					} else {
						this.hideLocation(l);
					}
				});
				break;
			case "HealthySchools":
				locations.forEach((l) => {
					if (
						(activeFiltersGrouped.length -
							(searchAddress ? 1 : 0) ===
							0 &&
							(l.type === "vbgf" || l.type === "sn21")) ||
						(activeCantons.length === 0 &&
							activeFiltersGrouped.length -
								(searchAddress ? 1 : 0) ===
								1 &&
							l.type === "standard") ||
						(activeCantons.length > 0 &&
							activeFiltersGrouped.length -
								(searchAddress ? 1 : 0) ===
								1) ||
						(activeCantons.length > 0 &&
							activeFiltersGrouped.length -
								(searchAddress ? 1 : 0) >
								1 &&
							l.type === "standard")
					) {
						this.searchCondition(
							l,
							activeFilters,
							activeFiltersGrouped,
							searchAddress,
							hasSearchBounds,
						);
					} else {
						this.hideLocation(l);
					}
				});
				break;
			default:
				locations.forEach((l) => {
					this.searchCondition(
						l,
						activeFilters,
						activeFiltersGrouped,
						searchAddress,
						hasSearchBounds,
					);
				});
				break;
		}
	}

	private searchCondition(
		l,
		activeFilters,
		activeFiltersGrouped,
		searchAddress,
		hasSearchBounds,
	) {
		if (
			activeFilters.length === 0 ||
			(searchAddress &&
				activeFilters.length === 1 &&
				(hasSearchBounds ||
					l.search.some((o) => o.indexOf(searchAddress) >= 0))) ||
			(!searchAddress &&
				activeFilters.length >= 1 &&
				activeFiltersGrouped.every((group) =>
					group[1].some((o) => l.tags.indexOf(o) >= 0),
				)) ||
			(activeFilters.length > 1 &&
				searchAddress &&
				activeFiltersGrouped.every(
					(group) =>
						group[1].some((o) => l.tags.indexOf(o) >= 0) ||
						(group[0] == "searchFilterTerm" &&
							(hasSearchBounds ||
								group[1].some((o) =>
									l.search.some((s) => s.indexOf(o) >= 0),
								))),
				) &&
				(hasSearchBounds ||
					l.search.some((o) => o.indexOf(searchAddress) >= 0)))
		) {
			this.showLocation(
				l,
				hasSearchBounds == false && activeFiltersGrouped.length > 0,
			);
		} else {
			this.hideLocation(l);
		}
	}

	private showLocation(l: MapsLocation, hit: boolean = false) {
		l.visible = true;
		removeClass(l.element, "d-none");
		toggleClass(l.element, "c-map-filter__list--hit", hit);
		l.marker.setIcon(
			this.getMarkerTemplate(
				hit
					? l.markerSettings.iconColorHit
					: l.markerSettings.iconColor,
				l.markerSettings.iconUrl,
			),
		);
		if ("Cookiebot" in window && Cookiebot.consent.marketing) {
			l.marker.setMap(this.map);
			if (this.options.hideCluster == false) {
				this.markerCluster.addMarker(l.marker);
			}
		}
	}

	private hideLocation(l: MapsLocation) {
		l.visible = false;
		addClass(l.element, "d-none");
		removeClass(l.element, "c-map-filter__list--hit");
		if (hasClass(l.element, "is-active")) {
			this.hideCollapse(l);
		}
		if ("Cookiebot" in window && Cookiebot.consent.marketing) {
			l.marker.setMap(null);
			if (this.options.hideCluster == false) {
				this.markerCluster.removeMarker(l.marker);
			}
		}
	}

	private centerMap(bounds?, isExactPos = false) {
		if (
			"Cookiebot" in window &&
			Cookiebot.consent.marketing &&
			this.locations &&
			this.locations.length > 0
		) {
			if (!bounds) {
				bounds = this.accumulateLocationBounds(this.locations);
			}
			let top = 7.5;
			let left = 7.5;
			let bottom = 7.5;
			let right = 7.5;
			if (window.innerWidth > 767) {
				top = 15;
				left = this.mapOverlay.offsetWidth;
				right = 15;
				bottom = 15;
			}
			fitBoundsWithPadding(this.map, bounds, {
				top,
				left,
				bottom,
				right,
			});
			this.setZoom();
			if (isExactPos) {
				setTimeout(() => {
					const x = window.innerWidth > 767 ? -left / 2 : -left / 2;
					const y = window.innerWidth > 767 ? -top / 2 : bottom / 2;
					this.map.panBy(x, y);
				}, 100);
			}
		}
	}

	private setZoom() {
		if (this.map.getZoom() > this.options.maxZoom) {
			this.map.setZoom(this.options.maxZoom);
		}
	}

	private addRadiusToBounds(
		radius: number,
		bounds: google.maps.LatLngBounds,
	) {
		return this.expandBoundsByRadius(bounds, radius * 1000);
	}

	private accumulateLocationBounds(
		locations: MapsLocation[],
		allIfEmpty = true,
	) {
		let visibles = locations.filter((o) => o.visible);
		if (!visibles || visibles.length === 0) {
			if (allIfEmpty == false) {
				return null;
			}
			visibles = locations;
		}
		return visibles.reduce(
			(bounds, l) => bounds.extend(l.latLng),
			new google.maps.LatLngBounds(),
		);
	}

	private getSearchBounds(activeFilters: any[], activeCantons: any[]) {
		return new Promise<google.maps.LatLngBounds>((resolve) => {
			const address = this.getSearchAddress();
			if (address) {
				if (address === this.previousAddress) {
					resolve(
						new google.maps.LatLngBounds(
							this.previousBounds.getSouthWest(),
							this.previousBounds.getNorthEast(),
						),
					);
				} else {
					if (
						this.options.searchGeocode &&
						"Cookiebot" in window &&
						Cookiebot.consent.marketing
					) {
						this.geoCoder.geocode(
							{
								address,
								componentRestrictions: {
									country: "CH",
								},
								region: "ch",
							},
							(results, status) => {
								if (status === google.maps.GeocoderStatus.OK) {
									const bounds = results[0].geometry.bounds;
									this.previousAddress = address;
									this.previousBounds =
										new google.maps.LatLngBounds(
											bounds.getSouthWest(),
											bounds.getNorthEast(),
										);
									resolve(bounds);
								} else {
									resolve(null);
								}
							},
						);
					} else {
						resolve(null);
					}
				}
			} else {
				resolve(null);
			}
		});
	}

	private getSearchAddress() {
		if (this.filter.search) {
			const address = this.filter.search.value;
			if (address) {
				return address.trim().toLowerCase();
			}
		}
		return "";
	}

	private getMarkerTemplate(color = "#009799", variant: string) {
		const marker = this.options.markerIcon;
		const template = `<?xml version="1.0" encoding="utf-8"?>
		<svg version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px"
			 viewBox="0 0 27 43"
			 style="enable-background:new 0 0 0 0 27 43;"
			 xml:space="preserve"
			 width="${this.options.markerIcon.size.width}px"
			 height="${this.options.markerIcon.size.height}px">
		<g>
			<path fill="${color}" d="M13.5,0L13.5,0C6,0,0,6,0,13.5c0,4.9,2.2,11.3,6.6,19.1c3.2,5.8,6.5,10.2,6.6,10.3c0.1,0.1,0.2,0.2,0.4,0.2c0,0,0,0,0,0
				c0.2,0,0.3-0.1,0.4-0.2c0,0,3.3-5,6.5-11c4.3-8.1,6.5-14.3,6.5-18.3C27,6,21,0,13.5,0z M19.7,13.8c0,3.4-2.8,6.2-6.2,6.2
				c-3.4,0-6.2-2.8-6.2-6.2s2.8-6.2,6.2-6.2C16.9,7.5,19.7,10.3,19.7,13.8z"/>
		</g>
		</svg>
		`;
		marker.url =
			"data:image/svg+xml;charset=UTF-8," + encodeURIComponent(template);
		return marker;
	}

	private showCollapse(location: MapsLocation, callback?: Function) {
		addClass(location.element, "is-active");
		if (location.markerSettings.iconColorActive) {
			location.marker.setIcon(
				this.getMarkerTemplate(
					location.markerSettings.iconColorActive,
					location.markerSettings.iconUrl,
				),
			);
		}

		const collapse = getSelectorElementFromElement(location.element);
		if (collapse) {
			if (hasClass(collapse, "inner-collapse--done") == false) {
				addClass(collapse, "inner-collapse--done");
				const innerCollapse =
					collapse.querySelectorAll<HTMLElement>(
						".js-collapse-inner",
					);
				innerCollapse.forEach((inner) => {
					new Collapse(inner);
				});
			}
		} else {
			window.location = location.element.getAttribute("href");
		}
		if (callback) {
			callback();
		}
	}

	private hideCollapse(location: MapsLocation, callback?: Function) {
		removeClass(location.element, "is-active");
		location.marker.setIcon(
			this.getMarkerTemplate(
				location.markerSettings.iconColor,
				location.markerSettings.iconUrl,
			),
		);

		if (callback) {
			callback();
		}
	}

	private getScrollTarget() {
		return window.innerWidth > 767 ? this.mapScroll : this.scrollWrapper;
	}

	private getScrollTop() {
		const target = this.getScrollTarget();
		if (target instanceof Window) {
			return target.scrollY;
		} else {
			return target.scrollTop;
		}
	}

	private expandBoundsByRadius(bounds, radiusInMeters) {
		const earthRadius = 6378137; // Earth's radius in meters
		const latLngNE = bounds.getNorthEast();
		const latLngSW = bounds.getSouthWest();

		// Calculate the latitude and longitude offsets
		const latOffset = (radiusInMeters / earthRadius) * (180 / Math.PI);
		const lngOffset =
			(radiusInMeters /
				(earthRadius *
					Math.cos(
						(((latLngNE.lat() + latLngSW.lat()) / 2) * Math.PI) /
							180,
					))) *
			(180 / Math.PI);

		// Create new bounds by expanding the original bounds
		const newNE = {
			lat: latLngNE.lat() + latOffset,
			lng: latLngNE.lng() + lngOffset,
		};
		const newSW = {
			lat: latLngSW.lat() - latOffset,
			lng: latLngSW.lng() - lngOffset,
		};

		return new google.maps.LatLngBounds(newSW, newNE);
	}
}
